import { Component, AfterViewChecked, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import { DataService, WorkflowContextService } from 'src/app/services';
import { Tag } from 'src/app/models/tag';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'wm-settings-tags',
  standalone: false,
  templateUrl: './settings-tags.component.html',
  styleUrls: ['./settings-tags.component.scss']
})
export class SettingsTagsComponent implements OnInit, AfterViewChecked, OnDestroy {
  tagList: Tag[] = [];
  clientId: string = '';
  isLoading = false;
  errorMessage = '';

  showAddInline = false;
  newTag = '';

  clientSub$: Subscription;

  confirmData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: true,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };
  private pendingDeleteId: string | null = null;

  @ViewChild('addInput') addInputRef!: ElementRef<HTMLInputElement>;

  constructor(
    private dataService: DataService,
    private context: WorkflowContextService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.context.client) {
      this.clientId = this.context.client.id;
      this.getTags();
    } else {
      this.clientSub$ = this.context.client$.subscribe(client => {
        this.clientId = client.id;
        this.getTags();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.clientSub$) {
      this.clientSub$.unsubscribe();
    }
  }

  getTags(): void {
    if (!this.clientId) {
      this.errorMessage = 'Client ID is required';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.dataService.getTags(this.clientId).subscribe({
      next: (response: Tag[]) => {
        this.tagList = (response || []).map(t => ({ ...t }));
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching tags:', error);
        this.errorMessage = 'Failed to load tags. Please try again.';
        this.toastr.error('Failed to load tags.');
        this.isLoading = false;
      }
    });
  }

  openAddTag(): void {
    this.showAddInline = true;
    this.newTag = '';
  }

  cancelAdd(): void {
    this.showAddInline = false;
    this.newTag = '';
    this.errorMessage = '';
  }

  saveNewTag(): void {
    const trimmed = (this.newTag || '').trim();
    if (!trimmed) {
      this.errorMessage = 'Tag name cannot be empty';
      return;
    }

    const tempId = 'temp-' + Date.now();

    const tagObj: Tag = new Tag();
    tagObj.id = tempId;
    tagObj.name = trimmed;
    tagObj.clientId = this.clientId;

    this.tagList.push(tagObj);

    const payloadForApi = {
      name: trimmed,
      clientId: this.clientId,
      $id: '0' 
    };

    this.dataService.saveTag(payloadForApi as unknown as Tag).subscribe({
      next: (response: Tag) => {
        const idx = this.tagList.findIndex(t => t.id === tempId);
        if (idx !== -1) {
          this.tagList[idx] = {
            ...this.tagList[idx],
            ...response
          };
        } else {
          this.tagList.push(response);
        }
        this.toastr.success('Tag added successfully');
      },
      error: (error) => {
        console.error('Failed to add tag', error);
        this.errorMessage = 'Failed to add tag';
        this.toastr.error('Failed to add tag');
        this.tagList = this.tagList.filter(t => t.id !== tempId);
      }
    });

    this.showAddInline = false;
    this.newTag = '';
    this.errorMessage = '';
  }

  requestDelete(tagId: string, tagName: string): void {
    this.pendingDeleteId = tagId;
    this.confirmData = {
      ...this.confirmData,
      open: true,
      modalDescp: `Are you sure you want to delete the tag "${tagName}"?`,
      modalSubDescp: 'This action cannot be undone.',
      yesNoBtn: true,
      replaceYesWith: 'DELETE',
      replaceNoWith: 'CANCEL',
    };
  }

  confirmDelete(): void {
    if (!this.pendingDeleteId) {
      this.closeConfirm();
      return;
    }

    const found = this.tagList.find(t => t.id === this.pendingDeleteId);
    const tagToDelete: Tag = found ? found : (() => {
      const t = new Tag();
      t.id = this.pendingDeleteId as string;
      t.clientId = this.clientId;
      t.name = '';
      return t;
    })();

    this.tagList = this.tagList.filter(tag => tag.id !== this.pendingDeleteId);

    this.dataService.deleteTag(tagToDelete).subscribe({
      next: () => {
        this.toastr.success('Tag deleted successfully');
        this.getTags();
      },
      error: (error) => {
        console.error('Failed to delete tag', error);
        this.errorMessage = 'Failed to delete tag';
        this.toastr.error('Failed to delete tag');
        this.getTags();
      }
    });

    this.pendingDeleteId = null;
    this.closeConfirm();
  }

  cancelDelete(): void {
    this.pendingDeleteId = null;
    this.closeConfirm();
  }

  private closeConfirm(): void {
    this.confirmData = { ...this.confirmData, open: false };
  }

  retry(): void {
    this.getTags();
  }

  ngAfterViewChecked(): void {
    if (this.showAddInline && this.addInputRef?.nativeElement) {
      try {
        this.addInputRef.nativeElement.focus();
      } catch (err) {
      }
    }
  }
}